import { Api } from '@/api/api.client';
import type { Components } from '@/api/generated/api';

export async function fetchBillingProducts(): Promise<Components.Schemas.ProductTypeDto[]> {
  const res = await Api.Routes.Billing.getBillingProducts({});
  return res.rows;
}

/**
 * The per-market price catalog. Without a countryId the backend resolves the caller's tenant
 * context (locked-aware market currency); with one it prices that country's market — used by the
 * onboarding checkout, where the SCHOOL's country decides, not the buyer's tenant.
 */
export async function fetchMarketPricing(countryId?: number): Promise<Components.Schemas.BillingMarketPricingDtoData> {
  const res = await Api.Routes.Billing.getBillingMarketPricing(countryId != null ? { countryId } : {});
  return res.data;
}

export async function fetchUpcomingBillingAmount(): Promise<{ amountCents: number; currency: string | null }> {
  const res = await Api.Routes.Billing.getUpcomingBillingAmount({});
  return { amountCents: res.upcomingAmount, currency: res.upcomingCurrency ?? null };
}

export async function fetchTenantHasUsablePaymentMethod(): Promise<boolean> {
  const res = await Api.Routes.Billing.tenantHasUsablePaymentMethod();
  return res.hasUsableMethod;
}

export async function fetchTenantInvoices(): Promise<Components.Schemas.BillingInvoiceDto[]> {
  const res = await Api.Routes.Billing.getTenantInvoices();
  return res.rows;
}

export async function fetchTenantOffers(): Promise<Components.Schemas.BillingOfferDto[]> {
  const res = await Api.Routes.Billing.getTenantOffers();
  return res.rows;
}

export async function fetchTenantCheckoutOptions(): Promise<Components.Schemas.BillingCheckoutOptionsDtoData> {
  const res = await Api.Routes.Billing.getTenantCheckoutOptions();
  return res.data;
}

export async function fetchTenantScheduledPlanChange(): Promise<Components.Schemas.BillingScheduledPlanChangeDtoData> {
  const res = await Api.Routes.Billing.getTenantScheduledPlanChange();
  return res.data;
}

/** The purchased products and, once the first invoice is paid, the purchase of a hosted checkout. */
export async function fetchCheckoutSummary(checkoutSessionId: string): Promise<Components.Schemas.CheckoutSummaryDto> {
  const res = await Api.Routes.Billing.getCheckoutSummary({ checkoutSessionId });
  return res.data;
}

export async function switchTenantPlan(body: {
  productTypeId: number;
  quantity?: number;
  billingInterval?: Components.Schemas.BillingInterval;
}): Promise<void> {
  await Api.Routes.Billing.tenantSwitchPlan({}, body);
}

export async function markTenantTrialExpiredSeen(): Promise<void> {
  await Api.Routes.Billing.markTenantTrialExpiredSeen();
}

export async function extendTenantTrial(): Promise<void> {
  await Api.Routes.Billing.extendTenantTrial();
}

export async function checkoutTenant(body: {
  productTypeId: number;
  quantity?: number;
  returnPath?: string;
  source?: Components.Schemas.CheckoutSource;
  billingInterval?: Components.Schemas.BillingInterval;
}): Promise<string | null> {
  const res = await Api.Routes.Billing.checkoutTenant({}, body);
  return res.url;
}

export async function previewTenantCheckout(body: {
  productTypeId: number;
  quantity?: number;
  source?: Components.Schemas.CheckoutSource;
  billingInterval?: Components.Schemas.BillingInterval;
}): Promise<Components.Schemas.BillingCheckoutPreviewDtoData> {
  const res = await Api.Routes.Billing.checkoutTenantPreview({}, body);
  return res.data;
}

export async function previewTenantPlanSwitch(body: {
  productTypeId: number;
  quantity?: number;
  billingInterval?: Components.Schemas.BillingInterval;
}): Promise<Components.Schemas.BillingPlanSwitchPreviewDtoData> {
  const res = await Api.Routes.Billing.tenantSwitchPlanPreview({}, body);
  return res.data;
}

export async function fetchTenantInvoiceAddress(): Promise<Components.Schemas.BillingInvoiceAddressDto> {
  const res = await Api.Routes.Billing.getTenantInvoiceAddress();
  return res.data;
}

export async function updateTenantInvoiceAddress(
  address: Components.Schemas.UpsertTenantInvoiceAddressDto,
): Promise<Components.Schemas.BillingInvoiceAddressDto> {
  const res = await Api.Routes.Billing.updateTenantInvoiceAddress({}, address);
  return res.data;
}

export async function checkoutTenantViaInvoice(body: {
  productTypeId: number;
  quantity?: number;
  source?: Components.Schemas.CheckoutSource;
  billingInterval?: Components.Schemas.BillingInterval;
}): Promise<Components.Schemas.BillingInvoiceDto> {
  const res = await Api.Routes.Billing.checkoutTenantViaInvoice({}, body);
  return res.data;
}

export async function createTenantOffer(body: {
  productTypeId: number;
  quantity?: number;
  address: Components.Schemas.UpsertTenantInvoiceAddressDto;
}): Promise<Components.Schemas.BillingOfferDto> {
  const res = await Api.Routes.Billing.createTenantOffer({}, body);
  return res.data;
}

export async function updateTenantOfferAddress(
  offerId: number,
  address: Components.Schemas.UpsertTenantInvoiceAddressDto,
): Promise<Components.Schemas.BillingOfferDto> {
  const res = await Api.Routes.Billing.updateTenantOfferAddress({ offerId }, address);
  return res.data;
}

export async function acceptTenantOfferViaInvoice(offerId: number): Promise<Components.Schemas.BillingOfferDto> {
  const res = await Api.Routes.Billing.acceptTenantOfferViaInvoice({ offerId });
  return res.data;
}

export async function acceptTenantOfferOnline(offerId: number, returnPath: string): Promise<string | null> {
  const res = await Api.Routes.Billing.acceptTenantOfferOnline({ offerId }, { returnPath });
  return res.url;
}

export async function fetchTenantOfferPdf(offerId: number): Promise<Blob> {
  const res = await Api.Routes.Billing.downloadTenantOfferPdf({ offerId });
  return new Blob([res as BlobPart], { type: 'application/pdf' });
}

export async function deleteTenantOffer(offerId: number): Promise<void> {
  await Api.Routes.Billing.deleteTenantOffer({ offerId });
}

/** Null when the tenant has no billing customer yet and therefore no customer portal. */
export async function fetchTenantPortalLink(returnPath: string): Promise<string | null> {
  const res = await Api.Routes.Billing.getTenantPortalLink({}, { returnPath });
  return res.url;
}
